import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { TooltipContentProps } from 'recharts/types/component/Tooltip'
import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { Member, Workflow } from 'api/types'
import { Icon } from 'components/Icon'
import { useProject } from 'components/Layout'
import { AvatarStack, Card, EmptyState, ErrorState, Spinner } from 'components/ui'
import { ARABIC_MONTHS, PROJECT_STATUS_LABELS, ROLE_LABELS, WORKFLOW_LABELS, addDays, daysBetween, formatDate } from 'lib/format'
import { useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { activeLocale } from 'lib/region'
import { printWeekday } from 'pages/report/print-locale'
import { fetchWeather, weatherIcon, workAlert } from 'lib/weather'

const siteEngineerCountLabel = (count: number) => {
  if (count === 2) return t('مهندسا موقع', 'site engineers')
  if (count >= 3 && count <= 10) return t('مهندسي موقع', 'site engineers')
  return t('مهندس موقع', count === 1 ? 'site engineer' : 'site engineers')
}

const dayLabel = (iso: string, today?: string) =>
  today && iso === today
    ? t('اليوم', 'Today')
    : new Intl.DateTimeFormat(activeLocale() === 'en' ? 'en' : 'ar', { timeZone: 'Asia/Amman', weekday: 'long' }).format(new Date(`${iso}T12:00:00Z`))

const WORKFLOW_BADGE_CLASS: Record<Workflow, string> = {
  APPROVED: 'badge badge--complete',
  DRAFT: 'badge badge--draft',
  EMPTY: 'badge badge--new',
  RETURNED: 'badge badge--missing',
  SUBMITTED: 'badge badge--created',
}

const sameLocalDay = (iso: string, dateOnly: string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Amman' }).format(new Date(iso)) === dateOnly

const planStatus = (actual: number, planned: number) => {
  const delta = Math.round((actual - planned) * 100)
  if (delta > 2) return { delta, label: t(`متقدم ${delta} نقطة عن الخطة`, `${delta} points ahead of plan`), tone: 'plan-ahead' as const }
  if (delta < -2) return { delta, label: t(`متأخر ${Math.abs(delta)} نقطة عن الخطة`, `${Math.abs(delta)} points behind plan`), tone: 'plan-behind' as const }
  return { delta, label: t('وفق الخطة', 'On plan'), tone: 'plan-onplan' as const }
}

const ChartTooltip = ({ active, payload, label }: TooltipContentProps) => {
  if (!active || !payload?.length) return null
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip__row">
        <span className="chart-tooltip__label">{label}</span>
      </div>
      {payload.map((entry) => (
        <div className="chart-tooltip__row" key={entry.dataKey as string}>
          <span className="chart-tooltip__label">{entry.name}</span>
          <strong>{Math.round(Number(entry.value))}%</strong>
        </div>
      ))}
    </div>
  )
}

/** Project manager / admin landing tab for a site — the site's own profile, distinct from the daily-reports
 *  index (Figma: Admin / 03 · Site detail — Overview). An issue tracker and document library are still out of
 *  scope — no such entities exist in this app. Weather uses Open-Meteo (free, keyless) against the project's
 *  own map location, set in Settings. */
export const SiteOverviewPage = () => {
  const { project, isManager } = useProject()
  const { data: today } = useAsync(api.fetchToday, [])
  const clampedTo = today ? (today > project.endDate ? project.endDate : today) : undefined

  const { data: members } = useAsync(() => api.fetchMembers(project.id), [project.id])

  const { data: forecast } = useAsync(
    () => (project.latitude != null && project.longitude != null ? fetchWeather(project.latitude, project.longitude) : Promise.resolve(undefined)),
    [project.latitude, project.longitude]
  )
  const alertsThisWeek = forecast?.filter((day) => workAlert(day)).length ?? 0
  const pmNames = (members ?? []).filter((member: Member) => member.role === 'PROJECT_MANAGER').map((member) => member.user.name)
  const engineerNames = (members ?? []).filter((member: Member) => member.role === 'SITE_ENGINEER').map((member) => member.user.name)

  const { data: summary, error: summaryError, loading: summaryLoading, reload: reloadSummary } = useAsync(
    () => (clampedTo ? api.fetchSummary(project.id, project.startDate, clampedTo) : Promise.resolve(undefined)),
    [project.id, project.startDate, clampedTo]
  )

  const {
    data: allReports,
    error: reportsError,
    loading: reportsLoading,
    reload: reloadReports,
  } = useAsync(() => (clampedTo ? api.fetchReports(project.id, project.startDate, clampedTo) : Promise.resolve(undefined)), [
    project.id,
    project.startDate,
    clampedTo,
  ])

  const todayItem = (allReports ?? []).find((item) => item.reportDate === today && item.workflow !== 'EMPTY')
  const { data: todayFull } = useAsync(() => (todayItem ? api.fetchReport(todayItem.id) : Promise.resolve(undefined)), [todayItem?.id])
  const crewToday = todayFull ? todayFull.staff.reduce((sum, line) => sum + line.count, 0) : null

  const counts = new Map(summary?.statusCounts.map((item) => [item.status, item.count]))
  const complete = (counts.get('COMPLETE') ?? 0) + (counts.get('CREATED') ?? 0)
  const missing = counts.get('MISSING') ?? 0
  const due = summary ? summary.workingDays + missing : 0
  const actual = due ? complete / due : null
  const totalDays = Math.max(1, daysBetween(project.startDate, project.endDate) + 1)
  const elapsedDays = today ? Math.max(0, daysBetween(project.startDate, clampedTo ?? project.startDate) + 1) : 0
  const planned = Math.min(1, elapsedDays / totalDays)
  const plan = actual != null ? planStatus(actual, planned) : undefined

  const last30 = today ? (allReports ?? []).filter((item) => item.reportDate >= addDays(today, -29) && item.reportDate <= today) : []
  const due30 = last30.length
  const onTime30 = last30.filter(
    (item) => (item.workflow === 'SUBMITTED' || item.workflow === 'APPROVED' || item.workflow === 'RETURNED') && item.submittedAt && sameLocalDay(item.submittedAt, item.reportDate)
  ).length
  const missing30 = last30.filter((item) => item.status === 'MISSING').length
  const onTimeRate = due30 ? onTime30 / due30 : null

  const chartData = useAsync(async () => {
    if (!today || !clampedTo || !allReports) return []
    const byDate = new Map(allReports.map((item) => [item.reportDate, item]))
    const points: { date: string; label: string; actual: number; submitted: number }[] = []
    let actualCum = 0
    let submittedCum = 0
    let index = 0
    for (let day = project.startDate; day <= clampedTo; day = addDays(day, 1)) {
      index += 1
      const item = byDate.get(day)
      if (item && (item.status === 'COMPLETE' || item.status === 'CREATED')) actualCum += 1
      if (item && (item.workflow === 'SUBMITTED' || item.workflow === 'APPROVED' || item.workflow === 'RETURNED')) submittedCum += 1
      points.push({
        actual: (actualCum / index) * 100,
        date: day,
        label: day.endsWith('-01') ? ARABIC_MONTHS[Number(day.slice(5, 7)) - 1] : '',
        submitted: (submittedCum / index) * 100,
      })
    }
    return points
  }, [allReports, project.startDate, clampedTo, today]).data

  const chartSeries = chartData?.map((point) => ({
    ...point,
    label: point.date.endsWith('-01') ? ARABIC_MONTHS[Number(point.date.slice(5, 7)) - 1] : '',
  }))
  const lastPoint = chartData?.[chartData.length - 1]

  const recentSorted = today
    ? (allReports ?? [])
        .filter((item) => item.reportDate >= addDays(today, -6))
        .slice()
        .sort((a, b) => (a.reportDate < b.reportDate ? 1 : -1))
    : []

  const loading = (summaryLoading && !summary) || (reportsLoading && !allReports)

  return (
    <div className="page">
      <div className="site-head">
        <div className="site-head__title">
          <h1>{project.name}</h1>
          <span className={`badge badge--project-${project.status.toLowerCase()}`}>{PROJECT_STATUS_LABELS[project.status]}</span>
        </div>
        <div className="site-head__meta">
          {project.location ? (
            <span>
              <Icon name="mapPin" size={14} /> {project.location}
            </span>
          ) : null}
          <span>
            <Icon name="calendar" size={14} /> {formatDate(project.startDate)} – {formatDate(project.endDate)}
          </span>
        </div>
        <div className="site-head__people">
          {pmNames.length ? (
            <div className="site-head__person">
              <AvatarStack names={pmNames} />
              <div className="site-head__person-text">
                <strong>{pmNames.join(t('، ', ', '))}</strong>
                <small>{ROLE_LABELS.PROJECT_MANAGER}</small>
              </div>
            </div>
          ) : null}
          {engineerNames.length ? (
            <div className="site-head__person">
              <AvatarStack names={engineerNames} max={3} />
              <div className="site-head__person-text">
                <strong>
                  {engineerNames.length} {siteEngineerCountLabel(engineerNames.length)}
                </strong>
                <small>{engineerNames.join(t('، ', ', '))}</small>
              </div>
            </div>
          ) : null}
        </div>
        {isManager ? (
          <Link to={`/projects/${project.id}/settings`} className="btn site-head__edit">
            <Icon name="edit" size={14} /> {t('تعديل الموقع', 'Edit site')}
          </Link>
        ) : null}
      </div>

      {loading ? (
        <Spinner />
      ) : summaryError ? (
        <ErrorState message={summaryError} onRetry={reloadSummary} />
      ) : reportsError ? (
        <ErrorState message={reportsError} onRetry={reloadReports} />
      ) : !summary ? null : (
        <>
          <div className="stats stats--static" role="group" aria-label={t('مؤشرات الموقع', 'Site indicators')}>
            <div className="stat">
              <span className="stat__value">{actual != null ? `${Math.round(actual * 100)}%` : '—'}</span>
              <span className="stat__label">{t('التقدم الإجمالي', 'Overall progress')}</span>
              {plan ? <span className={`small ${plan.tone}`}>{plan.label}</span> : null}
            </div>
            <div className="stat">
              <span className="stat__value">{crewToday != null ? crewToday : '—'}</span>
              <span className="stat__label">{t('الكوادر في الموقع اليوم', 'Crew on site today')}</span>
              {crewToday == null ? <span className="small muted">{t('لا يوجد تقرير اليوم', 'No report for today')}</span> : null}
            </div>
            <div className="stat">
              <span className="stat__value">{onTimeRate != null ? `${Math.round(onTimeRate * 100)}%` : '—'}</span>
              <span className="stat__label">{t('تقارير مُرسلة في يومها (30 يوم)', 'Reports submitted the same day (30 days)')}</span>
              <span className="small muted">
                {onTime30} {t('من', 'of')} {due30}
              </span>
            </div>
            <div className="stat">
              <span className="stat__value">{missing30}</span>
              <span className="stat__label">{t('تقارير مفقودة (30 يوم)', 'Missing reports (30 days)')}</span>
            </div>
          </div>

          <div className="summary-grid">
            <Card title={t('التقدم — الفعلي مقابل المُرسل', 'Progress — actual vs submitted')} className="span-2">
              {chartSeries?.length ? (
                <>
                  <div className="status-chart__plot">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={chartSeries} margin={{ bottom: 0, left: 0, right: 0, top: 8 }}>
                        <CartesianGrid stroke="var(--chart-grid, #e5e7eb)" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: 'var(--ink-2)', fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: 'var(--line)' }}
                          interval={0}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                          tick={{ fill: 'var(--ink-2)', fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                          width={40}
                        />
                        <Tooltip content={ChartTooltip} />
                        <Line type="monotone" dataKey="actual" name={t('الفعلي', 'Actual')} stroke="var(--chart-1, var(--brand))" strokeWidth={2} dot={false} />
                        <Line
                          type="monotone"
                          dataKey="submitted"
                          name={t('المُرسل', 'Submitted')}
                          stroke="var(--chart-2)"
                          strokeDasharray="4 3"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {lastPoint ? (
                    <p className="muted small">
                      {t(
                        `اعتباراً من ${formatDate(lastPoint.date)}: الفعلي ${Math.round(lastPoint.actual)}% · المُرسل ${Math.round(lastPoint.submitted)}%`,
                        `As of ${formatDate(lastPoint.date)}: actual ${Math.round(lastPoint.actual)}% · submitted ${Math.round(lastPoint.submitted)}%`
                      )}
                    </p>
                  ) : null}
                </>
              ) : (
                <EmptyState title={t('لا تتوفر بيانات كافية بعد', 'Not enough data yet')} />
              )}
            </Card>

            <Card title={t('أحدث التقارير', 'Latest reports')} actions={<Link to={`/projects/${project.id}`} className="link-button">{t('عرض الكل', 'View all')}</Link>}>
              {!recentSorted.length ? (
                <EmptyState icon="file" title={t('لا توجد تقارير في آخر 7 أيام', 'No reports in the last 7 days')} />
              ) : (
                <ul className="today-recent">
                  {recentSorted.map((item) => (
                    <li className="today-recent__row" key={item.id}>
                      <span className="today-icon">
                        <Icon name="file" size={16} />
                      </span>
                      <span className="today-recent__body">
                        <Link to={`/projects/${project.id}/reports/${item.id}`} className="code-link">
                          {item.code}
                        </Link>
                        <span className="muted small">
                          {formatDate(item.reportDate)} · {printWeekday(activeLocale(), item.reportDate, item.dayName)} · {item.preparedByName || '—'}
                        </span>
                      </span>
                      <span className={WORKFLOW_BADGE_CLASS[item.workflow]}>{WORKFLOW_LABELS[item.workflow]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <Card
            title={t('توقعات الطقس لـ 7 أيام', '7-day weather forecast')}
            actions={alertsThisWeek ? <span className="chip chip--warning">{t(`${alertsThisWeek} تنبيه عمل هذا الأسبوع`, `${alertsThisWeek} work alerts this week`)}</span> : null}
          >
            {project.latitude == null || project.longitude == null ? (
              <EmptyState icon="mapPin" title={t('لم يتم تحديد الموقع الجغرافي لهذا المشروع', 'No map location is set for this project')}>
                {isManager ? <p className="muted small">{t('حدّد موقع المشروع على الخريطة من إعدادات المشروع لعرض توقعات الطقس.', 'Set the project location on the map in project settings to show the weather forecast.')}</p> : null}
              </EmptyState>
            ) : !forecast ? (
              <Spinner />
            ) : (
              <div className="weather-row">
                {forecast.map((day) => {
                  const alert = workAlert(day)
                  return (
                    <div key={day.date} className={alert ? 'weather-day weather-day--alert' : 'weather-day'}>
                      <strong>{dayLabel(day.date, today)}</strong>
                      <span className="muted small">{formatDate(day.date)}</span>
                      <Icon name={weatherIcon(day.weatherCode)} size={28} />
                      <span>
                        {day.tempMax}° <span className="muted">{day.tempMin}°</span>
                      </span>
                      <span className="muted small">{t(`${day.precipitationProbability}% هطول`, `${day.precipitationProbability}% rain`)}</span>
                      <span className="muted small">{t(`${day.windSpeedMax} كم/س رياح`, `${day.windSpeedMax} km/h wind`)}</span>
                      {alert ? <span className="weather-day__alert">{alert}</span> : null}
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
